/**
 * useRefreshOnFocus — universal "data is stale" refetch trigger.
 *
 * Mustapha 2026-04-21: "il manque le refresh entre les pages et chaque
 * sessions BetterNotes pour éviter les bug et les hard refresh obligatoire".
 *
 * Wires two signals into a single refetch invocation:
 *
 *   1. **Document visibility** — when the tab becomes visible again after
 *      the user switched to another tab (Cmd+Tab, mobile app switch, OS
 *      background) the cached state may be ≥ minutes old. We refetch.
 *
 *   2. **Window focus** — same idea but covers the case where the browser
 *      window itself was unfocused (clicking another app), which doesn't
 *      always trigger a visibilitychange event in some browsers.
 *
 * Both are debounced together via a single setTimeout(0) microtask so
 * rapid sequences (visibility + focus firing back-to-back, common pattern
 * on Mac) collapse into one refetch.
 *
 * Pass `enabled = false` to suppress (e.g. before the user is authed).
 *
 * Stable identity: the refetch ref is captured once per render so the
 * hook never re-binds its listeners when the parent re-renders.
 *
 * For pathname-based refetch (route change), use the separate
 * `useRefetchOnPathChange` hook below — it depends on react-router's
 * `useLocation()` and therefore is ONLY safe inside the Router subtree.
 * AuthContext is rendered ABOVE BrowserRouter (see App.tsx), so this
 * hook MUST stay router-free or it crashes at module-load time
 * (Sentry BETTERCLOSE-H, fixed 2026-04-21).
 */
import { useEffect, useRef } from 'react'
import { useInRouterContext, useLocation } from 'react-router-dom'

export interface UseRefreshOnFocusOptions {
  /** Disable the hook entirely without unmounting it (default true). */
  enabled?: boolean
  /** Refetch when the tab becomes visible again (default true). */
  onVisibility?: boolean
  /** Refetch when the window regains focus (default true). */
  onWindowFocus?: boolean
}

export function useRefreshOnFocus(
  refetch: () => void,
  options: UseRefreshOnFocusOptions = {},
): void {
  const {
    enabled = true,
    onVisibility = true,
    onWindowFocus = true,
  } = options

  // Capture the latest refetch in a ref so the listeners never go stale
  // even when the caller passes a fresh closure each render. Updated in
  // an effect (not during render) per react-hooks/refs lint.
  const refetchRef = useRef(refetch)
  useEffect(() => {
    refetchRef.current = refetch
  }, [refetch])

  const lastFiredRef = useRef<number>(0)

  useEffect(() => {
    if (!enabled) return

    // Coalesce rapid signal sequences (visibilitychange + focus often fire
    // within ~5ms of each other on Cmd+Tab back). 200ms guard.
    //
    // 2026-04-23 — the earlier version wrapped each fire() in an explicit
    // `await Promise.race([supabase.auth.getSession(), timeout(3s)])`
    // "warm-up" to defeat the post-background-tab query-hang. That warm-up
    // was DROPPED because it was the actual cause of the hang: with 4+
    // consumers (AuthContext, useRecallCalendar, useBetterNotesMinutes,
    // useRecordings, …) each instance fired its own parallel getSession()
    // at every tab-return, saturating the GoTrueClient auth mutex and
    // leaving the shared client in a stuck internal state. Subsequent
    // queries (including PostgREST selects) then hung indefinitely until
    // our own 10 s Promise.race timeout fired in the consumer.
    //
    // Supabase JS handles token refresh automatically via
    // `autoRefreshToken: true` + the built-in visibility handler, so the
    // manual warm-up added contention for zero functional benefit. The
    // SIGNED_IN-cascade guard in AuthContext (prevAccessTokenRef) covers
    // the remaining edge case where _recoverAndRefresh emits a synthetic
    // SIGNED_IN with an unchanged access_token.
    const fire = () => {
      const now = Date.now()
      if (now - lastFiredRef.current < 200) return
      lastFiredRef.current = now
      refetchRef.current()
    }

    const handleVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fire()
      }
    }
    const handleFocus = () => fire()

    if (onVisibility) document.addEventListener('visibilitychange', handleVisibility)
    if (onWindowFocus) window.addEventListener('focus', handleFocus)

    return () => {
      if (onVisibility) document.removeEventListener('visibilitychange', handleVisibility)
      if (onWindowFocus) window.removeEventListener('focus', handleFocus)
    }
  }, [enabled, onVisibility, onWindowFocus])
}

/**
 * useRefetchOnPathChange — refetch when the React Router pathname changes.
 *
 * Silently no-ops when rendered outside a Router (e.g. `renderHook` in unit
 * tests, or a stray consumer above BrowserRouter). Inside the Router subtree
 * it behaves exactly as before.
 *
 * Skips the very first render so we don't double-fetch on mount (the
 * consumer hook usually fetches itself on mount via its own useEffect).
 */
export function useRefetchOnPathChange(
  refetch: () => void,
  options: { enabled?: boolean } = {},
): void {
  const { enabled = true } = options

  const refetchRef = useRef(refetch)
  useEffect(() => {
    refetchRef.current = refetch
  }, [refetch])

  // `inRouter` never flips within a single mount (a component is either
  // inside a Router tree or not), so the branched `useLocation()` call
  // is stable per render. We still suppress the lint rule because it can't
  // see this invariant.
  const inRouter = useInRouterContext()
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const location = inRouter ? useLocation() : null
  const pathname = location?.pathname
  const firstPathRef = useRef(true)
  useEffect(() => {
    if (!enabled || !inRouter) return
    if (firstPathRef.current) {
      firstPathRef.current = false
      return
    }
    const id = setTimeout(() => refetchRef.current(), 0)
    return () => clearTimeout(id)
  }, [enabled, inRouter, pathname])
}
